import React from "react";

/**
 * Brief visual confirmation between match identification and navigation
 * to the artwork detail page. Shows the captured frame with Google-Lens-
 * style L-shaped corner brackets at the homography corners returned by
 * the engine — proves "we saw the artwork *here*" before the visitor's
 * eye moves to the result page.
 *
 * Pure presentational. Parent controls when to mount/unmount and how
 * long to wait before navigating.
 */
type Props = {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  corners: number[][];
};

const ARM_FRACTION = 0.12;
const STROKE_WIDTH = 6;

const normalize = (x: number, y: number): [number, number] => {
  const len = Math.hypot(x, y) || 1;
  return [x / len, y / len];
};

export const MatchConfirmation: React.FC<Props> = ({
  imageUrl,
  imageWidth,
  imageHeight,
  corners,
}) => {
  if (!corners || corners.length !== 4) return null;

  const edgeLengths = [0, 1, 2, 3].map((i) => {
    const [x1, y1] = corners[i];
    const [x2, y2] = corners[(i + 1) % 4];
    return Math.hypot(x2 - x1, y2 - y1);
  });
  const avgEdge = edgeLengths.reduce((a, b) => a + b, 0) / 4;
  const armLen = avgEdge * ARM_FRACTION;

  const brackets = corners.map((c, i) => {
    const prev = corners[(i + 3) % 4];
    const next = corners[(i + 1) % 4];
    const [cx, cy] = c;
    const [dpx, dpy] = normalize(prev[0] - cx, prev[1] - cy);
    const [dnx, dny] = normalize(next[0] - cx, next[1] - cy);
    const e1x = cx + dpx * armLen;
    const e1y = cy + dpy * armLen;
    const e2x = cx + dnx * armLen;
    const e2y = cy + dny * armLen;
    return {
      key: i,
      points: `${e1x},${e1y} ${cx},${cy} ${e2x},${e2y}`,
      delayMs: i * 40,
    };
  });

  return (
    <div style={overlayStyle}>
      <div
        style={{
          position: "relative",
          maxWidth: "100vw",
          maxHeight: "100vh",
          aspectRatio: `${imageWidth} / ${imageHeight}`,
        }}
      >
        <img
          src={imageUrl}
          alt=""
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
        <svg
          viewBox={`0 0 ${imageWidth} ${imageHeight}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {brackets.map((b) => (
            <polyline
              key={b.key}
              points={b.points}
              stroke="white"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              style={{
                opacity: 0,
                animation: `mc-fade-in 260ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                animationDelay: `${b.delayMs}ms`,
                filter: "drop-shadow(0 0 6px rgba(0,0,0,0.6))",
              }}
            />
          ))}
        </svg>
        <style>{`
          @keyframes mc-fade-in {
            from { opacity: 0 }
            to   { opacity: 1 }
          }
        `}</style>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
